import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { getAllUsers } from "services/services";
import { dashboardStats } from "services/userServices";
import { Bar, Pie, Line } from 'react-chartjs-2';

import {
  Card,
  CardBody,
  Col,
  Row,  
  Container,
  Badge,
  CardTitle,
  UncontrolledTooltip,
} from "reactstrap";

//i18n
import { withTranslation } from "react-i18next";
import "../../assets/scss/style.css";
import {
  handleClickCopy,
  longStringConvertToShort,
} from "admin/commonFunction";

const Dashboard = ({ t }) => {

  //meta title
  // document.title = "Dashboard";
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({ users: 0, agents: 0, brokers: 0 });
  let [incermentInd, setIncrementInd] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const [loaderStatus, setLoaderStatus] = useState(false);
  const history = useHistory();
  
  // Chart data
  const barChartData = {
    labels: ['Users', 'Agents', 'Brokers', 'Properties'],
    datasets: [
      {
        label: 'Count',
        data: [stats.users, stats.agents, stats.brokers, stats.properties],
        backgroundColor: [
          '#339966', // brand green
          '#2a9d8f', // teal
          '#264653', // deep slate
          '#88d4ab', // mint
        ],
        borderColor: [
          '#339966',
          '#2a9d8f',
          '#264653',
          '#88d4ab',
        ],
        borderWidth: 2,
      },
    ],
  };

  const pieChartData = {
    labels: ['Users', 'Agents', 'Brokers', 'Properties'],
    datasets: [
      {
        data: [stats.users, stats.agents, stats.brokers, stats.properties],
        backgroundColor: [
          '#339966',
          '#2a9d8f',
          '#264653',
          '#88d4ab',
        ],
        borderColor: ['#ffffff', '#ffffff', '#ffffff', '#ffffff'],
        borderWidth: 3,
      },
    ],
  };

  const lineChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Users',
        data: [stats.users * 0.8, stats.users * 0.85, stats.users * 0.9, stats.users * 0.95, stats.users, stats.users],
        borderColor: '#339966',
        backgroundColor: 'rgba(51, 153, 102, 0.15)',
        fill: true,
        tension: 0.4,
        borderWidth: 3,
      },
      {
        label: 'Properties',
        data: [stats.properties * 0.7, stats.properties * 0.8, stats.properties * 0.85, stats.properties * 0.9, stats.properties * 0.95, stats.properties],
        borderColor: '#2a9d8f',
        backgroundColor: 'rgba(42, 157, 143, 0.15)',
        fill: true,
        tension: 0.4,
        borderWidth: 3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'top',
    },
    title: {
      display: true,
      text: 'Dashboard Statistics',
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
        }
      }]
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'bottom',
    },
    title: {
      display: true,
      text: 'Distribution Overview',
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'top',
    },
    title: {
      display: true,
      text: 'Growth Trends',
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
        }
      }]
    },
  };

  const reports = [
    {
      title: "Number of Users",
      iconClass: "bx-group",
      description: String(stats.users ?? 0),
    },
    {
      title: "Numbers of Agents",
      iconClass: "bx-group",
      description: String(stats.agents ?? 0),
    },
    {
      title: "Number of Brokers",
      iconClass: "bx-group",
      description: String(stats.brokers ?? 0),
    },
    {
      title:"Number of Properties",
      iconClass:"bx bxs-home",
      description:String(stats.properties ?? 0),
    }
  ];

  const getDashboardInfo = async () => {
    try {
      setLoaderStatus(true);
      console.log("📊 Dashboard: Calling dashboardStats API...");
      const res = await dashboardStats();
      console.log("📊 Dashboard: Stats API response:", res);
      
      const payload = res?.data ?? res;
      
      setStats({
        users:  payload?.totalUsers?? 0,
        agents:payload?.totalAgents ??  0,
        brokers:  payload?.totalBrokers ??  0,
        properties:  payload?.totalListings  ?? 0,
      });
      console.log("📊 Dashboard: Stats set successfully");
      setLoaderStatus(false);
    } catch (error) {
      setLoaderStatus(false);
      console.error("❌ Dashboard Stats API Error:", error);
      console.error("Error details:", error?.response?.data || error?.message);
      
      // Set default values if API fails
      setStats({
        users: 0,
        agents: 0,
        brokers: 0,
        properties: 0,
      });
    }
  };

  useEffect(() => {
    getDashboardInfo();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content margin-custom">
        <Container fluid>
          <Row>
            <Col xl="12">
              <Row>
                {reports.map((report, key) => (
                  <Col md="4" key={"_col_" + key}>
                    <Card className="mini-stats-wid">
                      <CardBody>
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium">
                              {report.title}
                            </p>
                            <h4 className="mb-0">{report.description}</h4>
                          </div>
                          <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                              <span
                              className="avatar-title rounded-circle"
                              style={{ background: "var(--brand)" }}
                            >
                              <i
                                className={
                                  "bx " + report.iconClass + " font-size-24"
                                }
                              ></i>
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>

            {/* Charts Section */}
            <Col xl="12" className="mt-4">
              <Row>
                {/* Bar Chart */}
                <Col lg="6">
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-4">Statistics Comparison</CardTitle>
                      <div style={{ height: '300px' }}>
                        <Bar data={barChartData} options={chartOptions} />
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Pie Chart */}
                <Col lg="6">
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-4">Data Distribution</CardTitle>
                      <div style={{ height: '300px' }}>
                        <Pie data={pieChartData} options={pieChartOptions} />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>

            {/* Line Chart */}
            <Col xl="12" className="mt-4">
              <Card>
                <CardBody>
                  <CardTitle className="mb-4">Growth Trends</CardTitle>
                  <div style={{ height: '400px' }}>
                    <Line data={lineChartData} options={lineChartOptions} />
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* <Col lg="12">
              <Card>
                <CardBody className="border-bottom py-2">
                  <div>
                    <Row className="d-flex align-items-center">
                      <Col lg="4">
                        <h5 className="text-color-gredient">
                          {" "}
                          {"Recent Transactions"}{" "}
                        </h5>
                      </Col>
                      <Col
                        lg="8"
                        className="d-flex align-items-center justify-content-end"
                      ></Col>
                    </Row>
                  </div>
                </CardBody>
                <CardBody>
                  <Table
                    id="tech-companies-1"
                    className="table table-striped table-bordered"
                  >
                    <Thead>
                      <Tr>
                        <Th>Sr. No.</Th>
                        <Th>User Wallet Address</Th>
                        <Th>Amount</Th>
                        <Th>Transaction Hash</Th>
                        <Th>Type</Th>
                        <Th>Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {loaderStatus ? (
                        <Tr>
                          <Td colSpan="6"> {t("Loading")} ... </Td>
                        </Tr>
                      ) : data?.length != 0 ? (
                        data?.map((element, index) => {
                          currentPage > 1
                            ? (incermentInd = (currentPage - 1) * perPage + 1)
                            : 0;
                          return (
                            <tr key={index}>
                              <td>
                                <span className="co-name">
                                  {" "}
                                  {incermentInd + index}{" "}
                                </span>
                              </td>
                              <td>
                                {longStringConvertToShort(
                                  element?.walletAddress,
                                  20
                                )}{" "}
                                <button
                                  className="btn mx-1 p-0"
                                  id="copyTooltip"
                                  onClick={() =>
                                    handleClickCopy(element?.walletAddress)
                                  }
                                >
                                  <i className="bx bxs-copy-alt text-primary"></i>
                                </button>
                                <UncontrolledTooltip
                                  placement="top"
                                  target="copyTooltip"
                                >
                                  {"Copy"}
                                </UncontrolledTooltip>
                              </td>
                              <td>{element.amount} Finity</td>
                              <td>
                                {longStringConvertToShort(
                                  element?.transactionHash,
                                  20
                                )}{" "}
                                <button
                                  className="btn mx-1 p-0"
                                  onClick={() =>
                                    handleClickCopy(element?.transactionHash)
                                  }
                                  id="copyTooltip1"
                                >
                                  <i className="bx bxs-copy-alt text-primary"></i>
                                </button>
                                <UncontrolledTooltip
                                  placement="top"
                                  target="copyTooltip1"
                                >
                                  {"Copy"}
                                </UncontrolledTooltip>
                                <button
                                  id="viewTooltip"
                                  className="btn mx-1 p-0"
                                  onClick={() =>
                                    window.open(
                                      `https://etherscan.io/tx/${element?.transactionHash}`
                                    )
                                  }
                                >
                                  <i className="bx bxs-right-top-arrow-circle text-primary"></i>
                                </button>
                                <UncontrolledTooltip
                                  placement="top"
                                  target="viewTooltip"
                                >
                                  {"View"}
                                </UncontrolledTooltip>
                              </td>
                              <td>
                                <Badge
                                  color={
                                    element.tokenType == "Invested"
                                      ? "success"
                                      : "info"
                                  }
                                  className="me-1 p-1"
                                >
                                  {element.tokenType}
                                </Badge>
                              </td>
                              <td>
                                <Badge
                                  color={
                                    element.transferStatus == "sent"
                                      ? "info"
                                      : "success"
                                  }
                                  className="me-1 p-1"
                                >
                                  {element.transferStatus === "sent"
                                    ? "Completed"
                                    : element.transferStatus}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6}>No Records Found</td>
                        </tr>
                      )}
                    </Tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col> */}
            {/* <Col xl="12">
              <ChartUser />
            </Col> */}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(Dashboard);
